import React, { useMemo, useContext, useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Link } from 'react-router-dom';

import NavbarToggle from 'components/NavbarToggle';
import TradeMark from 'components/TradeMark';
import StoreSelectDropdown from 'components/StoreSelectDropdown';
import { ReactComponent as ChevronLeft } from 'assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as SignOutIcon } from 'assets/icons/sign-out.svg';

import { navbar as TEXT } from 'locales/en';
import { logout } from 'redux/slices/auth';
import { useMediaQuery } from 'hooks';
import breakpoints from 'utils/breakpoints';
import { BrandContext } from 'utils/brand-context';
import NavigationLinks from './NavigationLinks';
import { setNavItemVisible } from 'redux/slices/sidebar';

import { PROFILE, navigationItemsByConfig } from './navigationItems';

const Navbar = ({ onCollapse, isCollapsed, isOnboarding }) => {
  const brand = useContext(BrandContext);
  const dispatch = useDispatch();
  const [isHamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const isLarge = useMediaQuery(breakpoints.lg);
  const {
    paymentLinks,
    disputeLinks,
    billingLinks,
    caboodleLinks,
    adminLinks,
    navItemVisible,
  } = useSelector((state) => state.sidebar);
  const NAVIGATION_ITEMS = useMemo(
    () => navigationItemsByConfig(brand),
    [brand]
  );

  useEffect(() => {
    if (isLarge) {
      setHamburgerMenuOpen(false);
    }
  }, [isLarge]);

  const closeHamburgerMenu = () => {
    if (isHamburgerMenuOpen) {
      setHamburgerMenuOpen(false);
    }
  };

  return (
    <div
      data-testid="navbar"
      className={`group navbar transition-width duration-200 z-30 ease-in-out ${
        isCollapsed ? 'lg:w-10' : 'lg:w-64'
      } ${
        isHamburgerMenuOpen
          ? 'fixed bg-white h-screen min-h-full w-full lg:relative'
          : `absolute lg:fixed top-0 left-0 bottom-0 bg-transparent lg:bg-white`
      }`}
    >
      {isCollapsed ? (
        <ChevronRight
          className="opacity-0 top-12 lg:opacity-100 w-6 h-6 cursor-pointer absolute p-1 -right-3 text-white fill-current bg-brand-primary rounded-full"
          onClick={onCollapse}
        />
      ) : (
        <ChevronLeft
          className="opacity-0 top-12 lg:opacity-0 lg:group-hover:opacity-100 w-6 h-6 cursor-pointer absolute p-1 -right-3 text-white fill-current bg-brand-primary rounded-full"
          onClick={onCollapse}
        />
      )}
      <NavbarToggle
        toggleMenu={() => setHamburgerMenuOpen(!isHamburgerMenuOpen)}
        isHamburgerMenuOpen={isHamburgerMenuOpen}
        hasHamburgerIcon={!isOnboarding}
        LogoIcon={brand.Logo}
        logoStyle={brand.logoStyle}
      />
      <div
        className={`${
          isHamburgerMenuOpen
            ? 'flex flex-col justify-between mt-8 h-full lg:hidden'
            : 'hidden'
        } ${isCollapsed ? 'lg:hidden' : 'lg:flex lg:flex-col lg:h-full'}`}
      >
        <div className="lg:h-auto flex-shrink-0 lg:my-12 hidden lg:block">
          <Link to={{ pathname: '/' }} className="outline-none">
            <brand.Logo className={brand.logoStyle} />
          </Link>
        </div>
        <StoreSelectDropdown selectCallback={closeHamburgerMenu} />
        <div className="h-full relative">
          <div className="absolute top-0 left-0 bottom-0 right-0 px-4">
            <div className="h-full flex flex-col justify-between pb-0 lg:pb-10">
              <div className="overflow-auto box-border mb-4">
                {!isOnboarding ? (
                  <>
                    <nav className="mb-4">
                      <NavLink
                        onClick={closeHamburgerMenu}
                        to={brand.dashboard.to}
                        className="nav-header"
                        isActive={(match, location) => {
                          if (!match) {
                            return false;
                          }

                          return (
                            match.isExact ||
                            NAVIGATION_ITEMS.some(
                              (item) => location.pathname === item.to
                            )
                          );
                        }}
                      >
                        {brand.dashboard.name}
                      </NavLink>
                      {NAVIGATION_ITEMS.map((item, idx) => (
                        <NavLink
                          key={item.name}
                          onClick={closeHamburgerMenu}
                          to={item.to}
                          className="nav-link"
                        >
                          {item.name}
                        </NavLink>
                      ))}
                    </nav>
                    <NavigationLinks
                      header={TEXT.payments}
                      links={paymentLinks}
                      onClickCb={closeHamburgerMenu}
                      canCollapse={false}
                    />
                    <NavigationLinks
                      header={TEXT.disputeFlow}
                      links={disputeLinks}
                      onClickCb={closeHamburgerMenu}
                      canCollapse={false}
                    />
                    <NavigationLinks
                      header={TEXT.billing}
                      links={billingLinks}
                      onClickCb={closeHamburgerMenu}
                      onCollapse={() =>
                        dispatch(
                          setNavItemVisible({
                            billing: !navItemVisible.billing,
                          })
                        )
                      }
                      isCollapsed={!navItemVisible.billing}
                    />
                    <NavigationLinks
                      header={TEXT.caboodle}
                      links={caboodleLinks}
                      onClickCb={closeHamburgerMenu}
                      onCollapse={() =>
                        dispatch(
                          setNavItemVisible({
                            caboodle: !navItemVisible.caboodle,
                          })
                        )
                      }
                      isCollapsed={!navItemVisible.caboodle}
                    />
                    <NavigationLinks
                      header={TEXT.admin}
                      links={adminLinks}
                      onClickCb={closeHamburgerMenu}
                      onCollapse={() =>
                        dispatch(
                          setNavItemVisible({
                            admin: !navItemVisible.admin,
                          })
                        )
                      }
                      isCollapsed={!navItemVisible.admin}
                    />
                  </>
                ) : (
                  <nav>
                    <NavLink to={brand.dashboard.to} className="nav-header">
                      {brand.dashboard.name}
                    </NavLink>
                  </nav>
                )}
              </div>
              <div className="mt-auto mb-12 lg:mb-0">
                {!isLarge && (
                  <div>
                    <NavLink
                      onClick={closeHamburgerMenu}
                      to={PROFILE.to}
                      className="nav-link font-normal"
                    >
                      {PROFILE.name}
                    </NavLink>
                    <div
                      className="inline-block cursor-pointer nav-link mt-0"
                      onClick={() => dispatch(logout())}
                      data-testid="logout"
                    >
                      <SignOutIcon className="w-5 h-5 mr-2 inline text-javelin-blueGray fill-current" />
                      <p className="font-normal inline">{TEXT.signOut}</p>
                    </div>
                  </div>
                )}
                <div className="px-4 fixed bottom-0">
                  <TradeMark />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Navbar.propTypes = {
  isCollapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  isOnboarding: PropTypes.bool,
};
Navbar.defaultProps = {
  isCollapsed: false,
  onCollapse: () => null,
  isOnboarding: false,
};

export default memo(Navbar);
